import { ProjectPanelRoute } from "@/types/routes";

const DEFAULT_TRANSACTION_INTERVAL_MS = 5000;

/** Poll interval for GET /transaction/project/{project-id} notification menu (ms). */
const PROJECT_TRANSACTION_NOTIFICATIONS_INTERVAL_MS = 10_000;

/** MUI snackbar / `toast*` default auto-hide (ms). */
const DEFAULT_SNACKBAR_AUTO_HIDE_DURATION_MS = 5000;

const DEFAULT_TAB_REDIRECT_PATH = ProjectPanelRoute.Script;

/** App chrome: main header bar height (px). */
const APP_HEADER_HEIGHT_PX = 64;

/** Project shell: left nav rail width when collapsed / expanded (px). */
const NAV_RAIL_COLLAPSED_WIDTH_PX = 64;
const NAV_RAIL_EXPANDED_WIDTH_PX = 240;

/** Project shell: sticky left tools column and right sidebar widths (px). */
const LEFT_TOOLS_PANEL_WIDTH_PX = 400;

/**
 * Style DNA creator + test controls (Figma left panel) — fluid width (`30vw` middle term).
 * Uses MUI breakpoints (media queries): below `xl` the clamp max is ~laptop-sized; from `xl` up the max is larger.
 * @see https://mui.com/material-ui/customization/breakpoints/
 */
const LEFT_TOOLS_STYLE_DNA_PANEL_WIDTH_MIN_PX = 500;
/** Typical laptop / sub-xl viewports — keeps the column near ~600px at common widths. */
const LEFT_TOOLS_STYLE_DNA_PANEL_WIDTH_CAP_DEFAULT_PX = 600;
/** Large viewports (`theme.breakpoints.up('xl')`, default ≥1536px) — allows ~800px before vw hits the cap. */
const LEFT_TOOLS_STYLE_DNA_PANEL_WIDTH_CAP_WIDE_PX = 800;
const LEFT_TOOLS_STYLE_DNA_PANEL_WIDTH_VW = 30;

/** Responsive CSS `width` for `LeftToolsPanel` when Style DNA is active (maps to `@media (min-width: …)` via MUI). */
const LEFT_TOOLS_STYLE_DNA_PANEL_WIDTH = {
  xs: `clamp(${LEFT_TOOLS_STYLE_DNA_PANEL_WIDTH_MIN_PX}px, ${LEFT_TOOLS_STYLE_DNA_PANEL_WIDTH_VW}vw, ${LEFT_TOOLS_STYLE_DNA_PANEL_WIDTH_CAP_DEFAULT_PX}px)`,
  xl: `clamp(${LEFT_TOOLS_STYLE_DNA_PANEL_WIDTH_MIN_PX}px, ${LEFT_TOOLS_STYLE_DNA_PANEL_WIDTH_VW}vw, ${LEFT_TOOLS_STYLE_DNA_PANEL_WIDTH_CAP_WIDE_PX}px)`
} as const;

const STYLE_DNA_MAIN_SPACE_MAX_WIDTH_PX = 1200;
const RIGHT_SIDEBAR_WIDTH_PX = 400;

/**
 * Shared collapse/expand animation for the left tools panel and right sidebar (Library) — both
 * push `MainSpace` via an animated `width` rather than overlaying, so they must share this exact
 * transition to look/feel identical from either side.
 */
const SIDE_PANEL_COLLAPSE_TRANSITION = "width 0.25s ease-in-out";

/** Timeline strip: header row inside `Timeline` (px). */
const TIMELINE_STRIP_HEADER_HEIGHT_PX = 32;

/** Timeline strip: thumbnail slot width (px). */
const TIMELINE_SLOT_WIDTH_PX = 142;

/** Timeline strip: thumbnail slot height (px). */
const TIMELINE_SLOT_HEIGHT_PX = 80;

/** Timeline strip: fixed width of insert (+) column between slots (px). */
const TIMELINE_INSERT_SEPARATOR_WIDTH_PX = 28;

/** Timeline reorder drag: horizontal edge band width for auto-scroll (px). */
const TIMELINE_DRAG_AUTO_SCROLL_EDGE_PX = 150;

/** Timeline reorder drag: max scroll delta per animation frame (px). */
const TIMELINE_DRAG_AUTO_SCROLL_MAX_DELTA_PX = 16;

/** Minimum allowed gap (seconds) between a clip's trim in/out points — shared by every trim-handle UI. */
const MIN_TRIM_GAP_SEC = 0.25;

/** MainSpace image zoom slider bounds (%). */
const MAIN_SPACE_ZOOM_MIN = 100;
const MAIN_SPACE_ZOOM_MAX = 400;

/** MainSpace media viewport minimum height (px). */
const MAIN_SPACE_MEDIA_MIN_HEIGHT_PX = 320;

/** When project has no `scenes[0]`, new shots default to this `scene-id`. */
const DEFAULT_STORYBOARD_SCENE_ID_FALLBACK = "scene-1";

/**
 * User video file uploads (MainSpace modal, Test Video Analysis). API accepts only
 * `video/mp4`; keep in sync with `normalizeVideoContentTypeForUpload` in `api.ts`.
 */
const USER_VIDEO_UPLOAD_FILE_EXTENSIONS = [".mp4"] as const;

/** `<input type="file" accept={…}>` value — MIME + extension (MP4 only). */
const USER_VIDEO_UPLOAD_ACCEPT = "video/mp4,.mp4" as const;

/** react-dropzone `accept` for MP4 uploads only. */
const USER_VIDEO_UPLOAD_DROPZONE_ACCEPT = {
  "video/mp4": [".mp4"]
} as const;

/** Extension (lowercase, includes dot) → MIME for POST /uploads/.../video `content_type`. */
const USER_VIDEO_UPLOAD_EXTENSION_TO_MIME: Readonly<Record<(typeof USER_VIDEO_UPLOAD_FILE_EXTENSIONS)[number], string>> = {
  ".mp4": "video/mp4"
};

/** Maximum file size for user MP4 uploads (20 MiB). Enforced in `api.ts` upload helpers. */
const MAX_USER_VIDEO_UPLOAD_BYTES = 20 * 1024 * 1024;

/**
 * Canonical display list for user-facing copy (PNG, JPG, JPEG, GIF, WEBP, BMP).
 * Must stay aligned with {@link USER_IMAGE_UPLOAD_DROPZONE_ACCEPT}.
 */
const USER_IMAGE_UPLOAD_FORMATS_DISPLAY = "PNG, JPG, JPEG, GIF, WEBP, BMP" as const;

/** react-dropzone `accept` for user image uploads — same family as {@link USER_IMAGE_UPLOAD_FORMATS_DISPLAY}. */
const USER_IMAGE_UPLOAD_DROPZONE_ACCEPT = {
  "image/png": [".png"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/gif": [".gif"],
  "image/webp": [".webp"],
  "image/bmp": [".bmp"]
} as const;

const USER_IMAGE_UPLOAD_REJECT_HINT = `Invalid file type. Use ${USER_IMAGE_UPLOAD_FORMATS_DISPLAY}.` as const;

const USER_IMAGE_UPLOAD_SUBTITLE_HINT = `Supported formats: { ${USER_IMAGE_UPLOAD_FORMATS_DISPLAY} }` as const;

const BOOK_UPLOAD_FORMATS_DISPLAY = "PDF, DOCX" as const;

const BOOK_UPLOAD_DROPZONE_ACCEPT = {
  "application/pdf": [".pdf"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"]
} as const;

const BOOK_UPLOAD_REJECT_HINT = `Unsupported file type. Use ${BOOK_UPLOAD_FORMATS_DISPLAY}.` as const;

/**
 * Maximum files for one multi-select upload (`FileUploadDropzonePanel` when `multiple`).
 * Default for `maxFiles`; merge, react-dropzone cap, and max toast use this value.
 */
const FILE_UPLOAD_MULTIPLE_MAX_FILES = 14;

/** Hard cap on reference images for Style DNA extraction (`POST /workflow` StyleDNAExtraction). */
const STYLE_DNA_REFERENCE_IMAGES_MAX = 10;

/** UX guidance only — not enforced; matches backend `STYLE_DNA_EXTRACTION_RECOMMENDED_REFERENCE_IMAGES`. */
const STYLE_DNA_REFERENCE_IMAGES_RECOMMENDED = 4;

const FILE_UPLOAD_PREVIEW_MAX_HEIGHT_PX = 360;

/** Stored on storyboard shot `visuals[].model` for files uploaded from the Video tool MainSpace flow. */
const STORYBOARD_VIDEO_MODEL_USER_FILE_UPLOAD = "file_upload";

/** Presigned video URL → poster PNG (`videoPosterCapture`): max wait for `loadeddata` (ms). */
const VIDEO_POSTER_CAPTURE_LOAD_TIMEOUT_MS = 45_000;

/** Downscale width cap when sampling mean luminance (faster than full-frame readback). */
const VIDEO_POSTER_LUMA_SAMPLE_MAX_WIDTH_PX = 320;

/** Pixel stride when averaging luma from `ImageData` (higher = faster, coarser). */
const VIDEO_POSTER_LUMA_SAMPLE_STRIDE = 6;

/** Normalized luma 0–1; stop seeking early when a frame is bright enough (skip black intros). */
const VIDEO_POSTER_GOOD_ENOUGH_LUMA = 0.04;

/** `canvas.toBlob` quality for PNG poster upload (0–1). */
const VIDEO_POSTER_PNG_QUALITY = 0.92;

/** `File` type for PNG posters uploaded after capturing a frame from a video entity URL. */
const VIDEO_POSTER_UPLOAD_MIME = "image/png" as const;

export {
  APP_HEADER_HEIGHT_PX,
  BOOK_UPLOAD_DROPZONE_ACCEPT,
  BOOK_UPLOAD_FORMATS_DISPLAY,
  BOOK_UPLOAD_REJECT_HINT,
  DEFAULT_SNACKBAR_AUTO_HIDE_DURATION_MS,
  DEFAULT_STORYBOARD_SCENE_ID_FALLBACK,
  DEFAULT_TAB_REDIRECT_PATH,
  DEFAULT_TRANSACTION_INTERVAL_MS,
  PROJECT_TRANSACTION_NOTIFICATIONS_INTERVAL_MS,
  FILE_UPLOAD_MULTIPLE_MAX_FILES,
  FILE_UPLOAD_PREVIEW_MAX_HEIGHT_PX,
  LEFT_TOOLS_PANEL_WIDTH_PX,
  LEFT_TOOLS_STYLE_DNA_PANEL_WIDTH,
  LEFT_TOOLS_STYLE_DNA_PANEL_WIDTH_CAP_DEFAULT_PX,
  LEFT_TOOLS_STYLE_DNA_PANEL_WIDTH_CAP_WIDE_PX,
  LEFT_TOOLS_STYLE_DNA_PANEL_WIDTH_MIN_PX,
  LEFT_TOOLS_STYLE_DNA_PANEL_WIDTH_VW,
  MAIN_SPACE_MEDIA_MIN_HEIGHT_PX,
  MAIN_SPACE_ZOOM_MAX,
  MAIN_SPACE_ZOOM_MIN,
  MAX_USER_VIDEO_UPLOAD_BYTES,
  MIN_TRIM_GAP_SEC,
  STYLE_DNA_MAIN_SPACE_MAX_WIDTH_PX,
  NAV_RAIL_COLLAPSED_WIDTH_PX,
  NAV_RAIL_EXPANDED_WIDTH_PX,
  RIGHT_SIDEBAR_WIDTH_PX,
  SIDE_PANEL_COLLAPSE_TRANSITION,
  STORYBOARD_VIDEO_MODEL_USER_FILE_UPLOAD,
  STYLE_DNA_REFERENCE_IMAGES_MAX,
  STYLE_DNA_REFERENCE_IMAGES_RECOMMENDED,
  TIMELINE_DRAG_AUTO_SCROLL_EDGE_PX,
  TIMELINE_DRAG_AUTO_SCROLL_MAX_DELTA_PX,
  TIMELINE_INSERT_SEPARATOR_WIDTH_PX,
  TIMELINE_SLOT_HEIGHT_PX,
  TIMELINE_SLOT_WIDTH_PX,
  TIMELINE_STRIP_HEADER_HEIGHT_PX,
  USER_IMAGE_UPLOAD_DROPZONE_ACCEPT,
  USER_IMAGE_UPLOAD_FORMATS_DISPLAY,
  USER_IMAGE_UPLOAD_REJECT_HINT,
  USER_IMAGE_UPLOAD_SUBTITLE_HINT,
  USER_VIDEO_UPLOAD_ACCEPT,
  USER_VIDEO_UPLOAD_DROPZONE_ACCEPT,
  USER_VIDEO_UPLOAD_EXTENSION_TO_MIME,
  USER_VIDEO_UPLOAD_FILE_EXTENSIONS,
  VIDEO_POSTER_CAPTURE_LOAD_TIMEOUT_MS,
  VIDEO_POSTER_GOOD_ENOUGH_LUMA,
  VIDEO_POSTER_LUMA_SAMPLE_MAX_WIDTH_PX,
  VIDEO_POSTER_LUMA_SAMPLE_STRIDE,
  VIDEO_POSTER_PNG_QUALITY,
  VIDEO_POSTER_UPLOAD_MIME
};
